/**
 * Public Estimate View (Feb 2026) — vista pública SOLO LECTURA.
 * Se monta desde App.js cuando la URL trae `?e=<share_token>`.
 * Sin sesión, sin AuthProvider, sin ningún control de la app interna.
 * Consume GET /api/public/estimates/{token} (payload ya sanitizado).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { PublicGroupedItemsBody } from "@/components/PublicGroupedItems";
import { PartnerPointBlock } from "@/components/PartnerPointBlock";

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return String(iso).slice(0, 10);
  }
};

export default function PublicEstimateView({ token }) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/public/estimates/${encodeURIComponent(token)}`
        );
        setDoc(data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg,#f5f5f0)]">
        <div className="font-mono text-sm text-neutral-500 animate-pulse">
          Cargando documento…
        </div>
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div
        data-testid="public-estimate-error"
        className="min-h-screen flex items-center justify-center bg-[var(--bg,#f5f5f0)] p-6"
      >
        <div className="bg-white border-2 border-black p-8 max-w-md text-center">
          <div className="text-3xl mb-3">🔒</div>
          <h1 className="font-bold text-lg">Documento no disponible</h1>
          <p className="text-sm text-neutral-600 mt-2">
            Este enlace no es válido o el documento ya no está disponible.
            Contactá a quien te lo compartió para recibir un enlace nuevo.
          </p>
        </div>
      </div>
    );
  }

  const c = doc.company_info || {};
  const cust = doc.customer || {};
  const showDiscount = Number(doc.discount_amount || 0) > 0;

  return (
    <div
      data-testid="public-estimate-view"
      className="min-h-screen bg-[var(--bg,#f5f5f0)] py-8 px-4"
    >
      <div className="max-w-3xl mx-auto bg-white border-2 border-black">
        {/* Header empresa */}
        <div className="px-6 sm:px-8 py-6 border-b-2 border-black flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            {c.logo_url && (
              <img
                src={c.logo_url}
                alt={c.name || "Logo"}
                className="w-16 h-16 object-contain"
              />
            )}
            <div>
              <div data-testid="public-est-company" className="font-black text-xl tracking-tight">
                {c.name || ""}
              </div>
              <div className="text-xs text-neutral-600">
                {[c.office_name, c.office_address].filter(Boolean).join(" · ")}
              </div>
              <div className="text-xs text-neutral-600">
                {[c.office_phone, c.office_email, c.website]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase">
              Cotización
            </div>
            <div data-testid="public-est-number" className="font-black text-2xl tracking-tight">
              {doc.number}
            </div>
            <div data-testid="public-est-date" className="text-xs text-neutral-600 mt-1">
              {fmtDate(doc.created_at)}
            </div>
          </div>
        </div>

        {/* Cliente */}
        <div className="px-6 sm:px-8 py-4 border-b border-neutral-200">
          <div className="font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase mb-1">
            Preparado para
          </div>
          <div data-testid="public-est-customer" className="font-bold">
            {cust.company || ""}
          </div>
          {cust.contact_person && (
            <div className="text-sm text-neutral-600">{cust.contact_person}</div>
          )}
          {doc.partner_point && (
            <div className="mt-3">
              <PartnerPointBlock pp={doc.partner_point} compact />
            </div>
          )}
        </div>

        {/* Items */}
        <div className="px-6 sm:px-8 py-4">
          <table data-testid="public-est-items" className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-black text-left">
                <th className="py-2 pr-2 font-mono text-[10px] tracking-[0.15em] uppercase text-neutral-500">
                  Descripción
                </th>
                <th className="py-2 px-2 font-mono text-[10px] tracking-[0.15em] uppercase text-neutral-500 text-right">
                  Cant.
                </th>
                <th className="py-2 px-2 font-mono text-[10px] tracking-[0.15em] uppercase text-neutral-500 text-right">
                  Precio
                </th>
                <th className="py-2 pl-2 font-mono text-[10px] tracking-[0.15em] uppercase text-neutral-500 text-right">
                  Total
                </th>
              </tr>
            </thead>
            <PublicGroupedItemsBody items={doc.items} testidPrefix="public-est" />
          </table>

          {/* Totales */}
          <div className="flex justify-end mt-4">
            <div className="w-full sm:w-72 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-600">Subtotal</span>
                <span data-testid="public-est-subtotal" className="font-mono">
                  {money(doc.subtotal)}
                </span>
              </div>
              {showDiscount && (
                <div className="flex justify-between">
                  <span className="text-neutral-600">Descuento</span>
                  <span data-testid="public-est-discount" className="font-mono">
                    −{money(doc.discount_amount)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-neutral-600">
                  Impuesto{doc.tax_exempt ? " (exento)" : doc.sales_tax_rate ? ` (${+(doc.sales_tax_rate * 100).toFixed(2)}%)` : ""}
                </span>
                <span data-testid="public-est-tax" className="font-mono">
                  {money(doc.sales_tax_amount)}
                </span>
              </div>
              <div className="flex justify-between border-t-2 border-black pt-2 mt-2">
                <span className="font-black text-base">TOTAL</span>
                <span data-testid="public-est-total" className="font-black font-mono text-base">
                  {money(doc.total_amount)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Notas para el cliente */}
        {doc.customer_notes && (
          <div className="px-6 sm:px-8 py-4 border-t border-neutral-200">
            <div className="font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase mb-1">
              Notas
            </div>
            <p data-testid="public-est-notes" className="text-sm whitespace-pre-wrap">
              {doc.customer_notes}
            </p>
          </div>
        )}

        <div className="px-6 sm:px-8 py-4 border-t-2 border-black text-center">
          <p className="text-xs text-neutral-500">
            Documento generado por {c.name || "GP"} · Solo lectura
          </p>
        </div>
      </div>
    </div>
  );
}
